import React, { useEffect, useRef } from "react";

/**
 * NebulaSphereCanvas
 * Drop into any React app. Renders a rotating particle “sphere” with mouse/touch interaction.
 *
 * Usage:
 * canvas<div style={{ height: 500 }}>
 *   <NebulaSphereCanvas />
 * </div>
 */
export default function NebulaSphereCanvas({
  particleCount = 3500,
  baseRadius = 450,
  interactionRadius = 100,
  warpStrength = 50,
  rotationSpeed = 0.1,
  disableInteractions = false,
  dprCap = 2, // cap devicePixelRatio for perf
}) {
  const canvasRef = useRef(null);
  const rafRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const SPRING = 0.05;
    const FRICTION = 0.01;
    const Z_PERSPECTIVE = 600;

    let ready = false;
    let particles = [];
    const mouse = { x: 0, y: 0, isActive: false };

    const resize = () => {
      const parent = canvas.parentElement;
      if (!parent) return false;

      const width = parent.clientWidth;
      const height = parent.clientHeight;

      // Layout not ready yet
      if (!width || !height) return false;

      const dpr = Math.min(window.devicePixelRatio || 1, dprCap);

      canvas.width = Math.floor(width * dpr);
      canvas.height = Math.floor(height * dpr);
      canvas.style.width = `${width}px`;
      canvas.style.height = `${height}px`;

      return true;
    };

    const initParticles = () => {
      particles = [];
      for (let i = 0; i < particleCount; i++) {
        // Uniform points on a sphere (Fibonacci / golden angle)
        const phi = Math.acos(1 - (2 * (i + 0.5)) / particleCount);
        const theta = Math.PI * (1 + Math.sqrt(5)) * i;

        const x = baseRadius * Math.sin(phi) * Math.cos(theta);
        const y = baseRadius * Math.sin(phi) * Math.sin(theta);
        const z = baseRadius * Math.cos(phi);

        particles.push({
          baseX: x,
          baseY: y,
          baseZ: z,
          x,
          y,
          z,
          vx: 0,
          vy: 0,
          vz: 0,
          size: Math.random() * 1.5 + 0.5,
          alpha: Math.random() * 0.5 + 0.5,
        });
      }
    };

    const getCY = (width, height) => {
      if (window.innerWidth > 991) {
        if (height > 900) return height * 1.1;
        if (height > 750) return height * 1.5;
        return height * 2.3;
      } else if (window.innerWidth > 767) {
        if (height > 900) return height * 1.2;
        if (height > 750) return height * 1.6;
        return height * 1.3;
      } else if (window.innerWidth > 480) {
        if (height > 750) return height * 1.8;
        return height * 2;
      } else {
        if (height > 650) return height * 2;
        return height * 2.4;
      }
    };

    const animate = () => {
      if (!ready) return;

      const width = canvas.width;
      const height = canvas.height;

      const cx = window.innerWidth < 768 ? width / 1.2 : width / 2;
      //   const cy = getCY(width, height);
      const cy = window.innerWidth < 768 ? height * 1.3 : getCY(width, height);

      ctx.clearRect(0, 0, width, height);

      const time = Date.now() * 0.001 * rotationSpeed;

      const scaleX = width / (canvas.offsetWidth || 1);
      const scaleY = height / (canvas.offsetHeight || 1);

      for (const p of particles) {
        // Base rotation around Y axis
        let tx = p.baseX * Math.cos(time) - p.baseZ * Math.sin(time);
        let tz = p.baseX * Math.sin(time) + p.baseZ * Math.cos(time);
        let ty = p.baseY;

        // Mouse-based subtle rotation
        if (mouse.isActive && !disableInteractions) {
          const mouseRelX = mouse.x * scaleX - cx;
          const mouseRelY = mouse.y * scaleY - cy;

          const rotX = mouseRelY * 0.0001;
          const rotY = mouseRelX * 0.0001;

          // rotate around Y
          let mx = tx * Math.cos(rotY) - tz * Math.sin(rotY);
          let mz = tx * Math.sin(rotY) + tz * Math.cos(rotY);
          tx = mx;
          tz = mz;

          // rotate around X
          let my = ty * Math.cos(rotX) - tz * Math.sin(rotX);
          mz = ty * Math.sin(rotX) + tz * Math.cos(rotX);
          ty = my;
          tz = mz;
        }

        // Spring back to target
        p.vx += (tx - p.x) * SPRING;
        p.vy += (ty - p.y) * SPRING;
        p.vz += (tz - p.z) * SPRING;

        // Project to screen
        const proj = Z_PERSPECTIVE / (Z_PERSPECTIVE + p.z);
        const sx = cx + p.x * proj;
        const sy = cy + p.y * proj;

        // Pointer warp
        if (mouse.isActive && !disableInteractions) {
          const dx = sx - mouse.x * scaleX;
          const dy = sy - mouse.y * scaleY;
          const dist = Math.sqrt(dx * dx + dy * dy);

          if (dist < interactionRadius) {
            const force = (interactionRadius - dist) / interactionRadius;
            const angle = Math.atan2(dy, dx);

            p.vx += Math.cos(angle) * force * warpStrength;
            p.vy += Math.sin(angle) * force * warpStrength;
            p.vz -= force * warpStrength * 0.5;
          }
        }

        // Integrate
        p.x += p.vx;
        p.y += p.vy;
        p.z += p.vz;

        // Friction
        p.vx *= FRICTION;
        p.vy *= FRICTION;
        p.vz *= FRICTION;

        const finalProj = Z_PERSPECTIVE / (Z_PERSPECTIVE + p.z);
        if (finalProj > 0) {
          const alpha = Math.min(1, Math.max(0.1, finalProj * p.alpha));
          ctx.beginPath();
          ctx.arc(sx, sy, p.size * finalProj, 0, Math.PI * 2);
          ctx.fillStyle = `rgba(0,0,0,${alpha})`;
          ctx.fill();
        }
      }

      rafRef.current = requestAnimationFrame(animate);
    };

    const safeInit = () => {
      if (!resize()) {
        setTimeout(safeInit, 100);
        return;
      }
      ready = true;
      initParticles();
      animate();
    };

    const onResize = () => {
      if (!ready) return;
      resize();
    };

    const onMove = (e) => {
      if (!ready) return;
      const rect = canvas.getBoundingClientRect();
      const clientX = e.touches ? e.touches[0].clientX : e.clientX;
      const clientY = e.touches ? e.touches[0].clientY : e.clientY;
      mouse.x = clientX - rect.left;
      mouse.y = clientY - rect.top;
      mouse.isActive = true;
    };

    const onLeave = () => {
      mouse.isActive = false;
    };

    window.addEventListener("resize", onResize);
    canvas.addEventListener("mousemove", onMove);
    canvas.addEventListener("mouseleave", onLeave);
    canvas.addEventListener("touchmove", onMove, { passive: false });
    canvas.addEventListener("touchend", onLeave);

    safeInit();

    return () => {
      ready = false;
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      window.removeEventListener("resize", onResize);
      canvas.removeEventListener("mousemove", onMove);
      canvas.removeEventListener("mouseleave", onLeave);
      canvas.removeEventListener("touchmove", onMove);
      canvas.removeEventListener("touchend", onLeave);
    };
  }, [
    particleCount,
    baseRadius,
    interactionRadius,
    warpStrength,
    rotationSpeed,
    disableInteractions,
    dprCap,
  ]);

  return (
    <canvas
      ref={canvasRef}
      style={{
        width: "100%",
        height: "100%",
        display: "block",
      }}
    />
  );
}
